import React, { useState, useEffect, Fragment } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { withRouter } from "react-router";
import { Card, CardSection, Button } from "cxa-components";
import {
  getEnrolmentCheckoutPreview,
  getEnrolmentBenefits,
  checkout,
  clearError,
} from "actions/enrollmentActions";
import { Loader } from "cxa-components";
import { injectIntl } from "react-intl";
import EnrollmentStepper from "./EnrollmentStepper";
import Wallet from "./WalletPP";
import UwIcon from "../../../assets/images/svg/uw_icon.svg";
import messages from "components/messagesDescription";
import { Checkbox } from "@cxagroup/casper";
import withMatchMediaProps from "@hocs/with-match-media-props";
import SelectedIcon from "../../../assets/images/svg/selected_icon.svg";
import { Prompt } from "react-router-dom";
import { formatMoney } from "./EnrollmentDataUtil";
import { benefitsErrorHandler } from "cxa-utils";
import { ErrorMessage } from "../../../components/common/ErrorMessage";

const AccountBalanceTable = ({ intl, data }) => {
  let creditPoints = 0;
  [
    ...data.planBasedConvertLeaveBenefits,
    ...data.sumAssuredBasedBenefits,
    ...data.planBasedBenefits,
    ...data.planBasedOptionalBenefits,
  ].forEach(x => {
    if (Number(x.price.numericValue) < 0) {
      creditPoints += Math.abs(x.price.numericValue);
    }
  });

  const utilizationAmount = Number(data.totalUtilization.numericValue) + creditPoints;
  const entitlementAmount = Number(data.totalEntitlement.numericValue) + creditPoints;
  const overPurchaseAmount = data.overPurchasePoints.numericValue || 0;
  const availableAmount = data.availablePoints.numericValue || 0;

  return (
    <table className="product-balance-table">
      <tbody>
        <tr>
          <td>
            <strong>{intl.formatMessage(messages.enrollment.yourSelectionDescription)}:</strong>
          </td>
          <td className="utilization-style" style={{ textAlign: "right" }}>
            <strong>{formatMoney(utilizationAmount)}</strong>
          </td>
        </tr>
        <tr>
          <td>
            <strong>{intl.formatMessage(messages.entitlementDescription)}:</strong>
          </td>
          <td style={{ textAlign: "right" }}>
            <strong>{formatMoney(entitlementAmount)}</strong>
          </td>
        </tr>
        <tr>
          <td>
            <strong>{intl.formatMessage(messages.enrollment.yourPayDescription)}:</strong>
          </td>
          <td className="over-purchase-style" style={{ textAlign: "right" }}>
            <strong>{formatMoney(overPurchaseAmount)}</strong>
          </td>
        </tr>
        <tr>
          <td>
            <strong>{intl.formatMessage(messages.enrollment.remainingBalanceDescription)}:</strong>
          </td>
          <td className="available-style" style={{ textAlign: "right" }}>
            <strong>{formatMoney(availableAmount)}</strong>
          </td>
        </tr>
      </tbody>
    </table>
  );
};
AccountBalanceTable.propTypes = {
  intl: PropTypes.func,
  data: PropTypes.object.isRequired,
};

const EnrollmentPreviewPage = ({ intl, isNarrowScreen, push, ...rest }) => {
  const dispatch = useDispatch();
  const [isDirty, setDirty] = useState(true);
  const [salaryDeductionAgreed, setSalaryDeductionAgreed] = useState(false);
  const [termAgreed, setTermAgreed] = useState(false);

  useEffect(() => {
    dispatch(getEnrolmentBenefits());
    dispatch(getEnrolmentCheckoutPreview());
  }, []);

  const enrollmentData = useSelector(state => state.enrollment);
  const steps = enrollmentData.settings.enrolmentPages;
  const hdfDisclaimer = intl.formatMessage({
    id: "config.hdfCheckoutWebpageDisclaimer",
    defaultMessage: enrollmentData.settings.hdfCheckoutWebpageDisclaimer,
  });
  const hdfDisclaimerMobile = intl.formatMessage({
    id: "config.hdfCheckoutMobileResponsiveDisclaimer",
    defaultMessage: enrollmentData.settings.hdfCheckoutMobileResponsiveDisclaimer,
  });
  const previewData = enrollmentData.preview;
  const details = enrollmentData.details;
  const error = enrollmentData.error;

  const reselectBenefits = () => {
    dispatch(clearError());
    setDirty(false);
    setTimeout(() => {
      push(`/shop/enrolment`);
    }, 200);
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const proceedCheckout = () => {
    setDirty(false);
    setTimeout(() => {
      dispatch(checkout(termAgreed, salaryDeductionAgreed, () => push(`/shop/checkout/success`)));
    }, 200);
  };

  const ProductRecord = ({ benefits, hideMemberList }) => {
    return isNarrowScreen && Array.isArray(benefits) && benefits.length > 0
      ? benefits.map(benefit => {
          return (
            <tr key={benefit.benefitID} className="divider-line">
              <td className="product-record">
                {benefit.userSelected && <SelectedIcon style={{ marginRight: "10px" }} />}
                <span className="product-name">
                  {intl.formatMessage({
                    id: benefit.benefitName,
                    defaultMessage: benefit.benefitName,
                  })}
                </span>
                <br />
                <span className="plan-name">
                  <strong>
                    {intl.formatMessage({
                      id: benefit.planName,
                      defaultMessage: benefit.planName,
                    })}
                  </strong>
                  {benefit.requiresUW && <UwIcon />}
                </span>
                <br />
                {!hideMemberList && (
                  <Fragment>
                    <span className="member-label">
                      {intl.formatMessage(messages.enrollment.selectedMembersDescription)}
                    </span>
                    <br />
                    {Array.isArray(benefit.members) &&
                      benefit.members.map(member => {
                        return (
                          <div className="member-name" key={member.key}>
                            <strong>{member.name}</strong>
                          </div>
                        );
                      })}
                  </Fragment>
                )}
              </td>
              <td style={{ textAlign: "right" }}>{benefit.price.value}</td>
            </tr>
          );
        })
      : benefits.map(benefit => {
          return (
            <tr key={benefit.benefitID}>
              <td>
                {benefit.userSelected && <SelectedIcon style={{ marginRight: "10px" }} />}
                <span>
                  {intl.formatMessage({
                    id: benefit.benefitName,
                    defaultMessage: benefit.benefitName,
                  })}
                </span>
              </td>
              <td className="product-record">
                <span className="plan-name">
                  <span>
                    <strong>
                      {intl.formatMessage({
                        id: benefit.planName,
                        defaultMessage: benefit.planName,
                      })}
                    </strong>
                  </span>
                  {benefit.requiresUW && <UwIcon />}
                </span>
                <br />
                {!hideMemberList && (
                  <Fragment>
                    <span className="member-label">
                      {intl.formatMessage(messages.enrollment.selectedMembersDescription)}
                    </span>
                    <br />
                    {Array.isArray(benefit.members) &&
                      benefit.members.map(member => {
                        return (
                          <div className="member-name" key={member.key}>
                            <strong>{member.name}</strong>
                          </div>
                        );
                      })}
                  </Fragment>
                )}
              </td>
              <td style={{ textAlign: "right" }}>{benefit.price.value}</td>
            </tr>
          );
        });
  };

  const BenefitSelectionTable = () =>
    isNarrowScreen ? (
      <table className="product-selection-table">
        <tbody>
          <tr className="divider-line">
            <th>
              <strong>{intl.formatMessage(messages.enrollment.productDescription)}</strong>
            </th>
            <th>
              <strong>
                {isNarrowScreen
                  ? intl.formatMessage(messages.priceDescription)
                  : intl.formatMessage(messages.enrollment.priceDescription)}{" "}
                ({previewData.enrolmentCurrency})
              </strong>
            </th>
          </tr>
          <ProductRecord benefits={previewData.sumAssuredBasedBenefits} />
          <ProductRecord benefits={previewData.planBasedBenefits} />
          <ProductRecord benefits={previewData.planBasedOptionalBenefits} />
          <ProductRecord
            benefits={previewData.planBasedConvertLeaveBenefits}
            hideMemberList={true}
          />
        </tbody>
      </table>
    ) : (
      <table className="product-selection-table">
        <tbody>
          <tr className="divider-line">
            <th>
              <strong>{intl.formatMessage(messages.enrollment.productDescription)}</strong>
            </th>
            <th>
              <strong> {intl.formatMessage(messages.enrollment.planDescription)}</strong>
            </th>
            <th>
              <strong>
                {intl.formatMessage(messages.enrollment.priceDescription)} (
                {previewData.enrolmentCurrency})
              </strong>
            </th>
          </tr>
          <ProductRecord benefits={previewData.sumAssuredBasedBenefits} />
          <ProductRecord benefits={previewData.planBasedBenefits} />
          <ProductRecord benefits={previewData.planBasedOptionalBenefits} />
          <ProductRecord
            benefits={previewData.planBasedConvertLeaveBenefits}
            hideMemberList={true}
          />
        </tbody>
      </table>
    );

  return enrollmentData.isFetching || enrollmentData.isSaving || !previewData || !details ? (
    <Loader />
  ) : (
    <div>
      <EnrollmentStepper steps={steps} index={steps.length - 1} />
      <Wallet {...rest} intl={intl} />
      <span className="page-header">
        <strong>
          {intl.formatMessage({
            id: steps[steps.length - 1].benefitTypeName,
            defaultMessage: steps[steps.length - 1].benefitTypeName,
          })}
        </strong>
      </span>
      <Card>
        <CardSection>
          <h4>
            <strong>
              {intl.formatMessage(messages.enrollment.reviewYourSelectionDescription)}
            </strong>
          </h4>
          <BenefitSelectionTable />
          <AccountBalanceTable data={previewData} intl={intl} />
        </CardSection>
      </Card>

      {Array.isArray(previewData.hdfForms) &&
        previewData.hdfForms.length > 0 &&
        previewData.hdfForms.find(x => x.id != "ManualHDF") && (
          <Card>
            <CardSection>
              <h4>
                <strong>{intl.formatMessage(messages.enrollment.eHDFRequiredDescription)}</strong>
                <UwIcon />
              </h4>
              {isNarrowScreen ? (
                <div dangerouslySetInnerHTML={{ __html: hdfDisclaimerMobile }}></div>
              ) : (
                <div dangerouslySetInnerHTML={{ __html: hdfDisclaimer }}></div>
              )}
              <div className="space-between"></div>
              <div>
                {previewData.hdfForms.map(x => {
                  return x.isMobileResponsive ? (
                    <Button
                      key="code"
                      type="button"
                      onClick={() => {
                        window.open(`/gen-hdf/${x.id}`, "_blank");
                      }}
                    >
                      {x.code}{" "}
                      {intl.formatMessage(messages.enrollment.healthDeclarationFormDescription)}
                    </Button>
                  ) : (
                    !isNarrowScreen && (
                      <Button
                        key="code"
                        type="button"
                        onClick={() => {
                          window.open(`/hdf/${x.id}`, "_blank");
                        }}
                      >
                        {x.code}{" "}
                        {intl.formatMessage(messages.enrollment.healthDeclarationFormDescription)}
                      </Button>
                    )
                  );
                })}
              </div>
            </CardSection>
          </Card>
        )}
      {previewData.overPurchasePoints &&
        previewData.overPurchasePoints.numericValue > 0 &&
        previewData.salaryDeductionNote && (
          <Card>
            <CardSection>
              <h4>
                <strong>
                  {intl.formatMessage(messages.enrollment.salaryDeductionDescription)}
                </strong>
              </h4>
              <Checkbox
                onChange={() => setSalaryDeductionAgreed(!salaryDeductionAgreed)}
                checked={salaryDeductionAgreed}
                className="member-list--label__item"
              >
                <div
                  dangerouslySetInnerHTML={{
                    __html: intl
                      .formatMessage({
                        id: "config.salaryDeductionNote",
                        defaultMessage: previewData.salaryDeductionNote,
                      })
                      .replace("[ExcessAmount]", previewData.overPurchasePoints.value),
                  }}
                ></div>
              </Checkbox>
            </CardSection>
          </Card>
        )}
      {(previewData.checkoutNote || previewData.productSummaryAgreement) && (
        <Card>
          <CardSection>
            <div
              dangerouslySetInnerHTML={{
                __html: intl.formatMessage({
                  id: "config.checkoutNote",
                  defaultMessage: previewData.checkoutNote,
                }),
              }}
            ></div>
            <div className="space-between"></div>
            <Checkbox
              onChange={() => setTermAgreed(!termAgreed)}
              checked={termAgreed}
              className="member-list--label__item"
            >
              <div
                dangerouslySetInnerHTML={{
                  __html: intl.formatMessage({
                    id: previewData.productSummaryAgreement,
                    defaultMessage: previewData.productSummaryAgreement,
                  }),
                }}
              ></div>
            </Checkbox>
          </CardSection>
        </Card>
      )}
      {error && error.message && error.messageKey && (
        <ErrorMessage
          error={benefitsErrorHandler(intl)({
            extraValue: error.extra,
            messageKey: error.messageKey,
            message: error.message,
          })}
          intl={intl}
        />
      )}
      <div
        className="btn-container"
        style={{
          justifyContent: "space-between",
        }}
      >
        <Button className="enrolment-btn" secondary type="button" onClick={reselectBenefits}>
          {intl.formatMessage(messages.enrollment.reselectBenefitSelection)}
        </Button>
        <Button
          type="button"
          className="enrolment-btn"
          disabled={
            (previewData.overPurchasePoints &&
              previewData.overPurchasePoints.numericValue > 0 &&
              !salaryDeductionAgreed) ||
            !termAgreed
          }
          onClick={proceedCheckout}
        >
          {intl.formatMessage(messages.enrollment.checkOutDescription)}
        </Button>
      </div>
      <Prompt
        when={isDirty}
        message={() => intl.formatMessage({ ...messages.unSaveBenefitMessage })}
      />
    </div>
  );
};
EnrollmentPreviewPage.propTypes = {
  push: PropTypes.func,
  intl: PropTypes.func,
  isNarrowScreen: PropTypes.bool,
};
// export default withRouter(injectIntl(EnrollmentPreviewPage));
export default withMatchMediaProps({
  isNarrowScreen: {
    maxWidth: 480,
  },
})(withRouter(injectIntl(EnrollmentPreviewPage)));



// WEBPACK FOOTER //
// ./components/employee/shop/EnrollmentPreviewPage.js